import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  buildDefaultStoredMenuPermissions,
  defaultMenuIdsByRole,
  readStoredMenuPermissions,
  writeStoredMenuPermissions,
  type StoredMenuPermissions,
} from '../utils/menuPermissionsStorage';

export const useMenuPermissions = () => {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState<StoredMenuPermissions>(() =>
    buildDefaultStoredMenuPermissions()
  );

  const loadPermissions = useCallback(() => {
    try {
      const merged = readStoredMenuPermissions();
      setPermissions(merged);
      writeStoredMenuPermissions(merged);
    } catch (error) {
      console.error('Erro ao carregar permissões:', error);
      const defaultPerms = buildDefaultStoredMenuPermissions();
      setPermissions(defaultPerms);
      writeStoredMenuPermissions(defaultPerms);
    }
  }, []);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  useEffect(() => {
    if (user) {
      loadPermissions();
    } else {
      const defaultPerms = buildDefaultStoredMenuPermissions();
      setPermissions(defaultPerms);
    }
  }, [user?.id, user?.role, loadPermissions]);

  const canAccess = (menuId: string): boolean => {
    if (menuId === 'ajuda') return true;
    if (!user?.role) return false;

    const rolePermissions = permissions[user.role];
    if (!rolePermissions) return false;

    const stored = rolePermissions[menuId];
    if (typeof stored === 'boolean') return stored;

    return defaultMenuIdsByRole[user.role]?.includes(menuId) ?? false;
  };

  const updatePermissions = (newPermissions: StoredMenuPermissions) => {
    setPermissions(newPermissions);
    writeStoredMenuPermissions(newPermissions);
  };

  const resetToDefaults = () => {
    const defaultPerms = buildDefaultStoredMenuPermissions();
    updatePermissions(defaultPerms);
  };

  return {
    permissions,
    canAccess,
    updatePermissions,
    resetToDefaults,
    defaultPermissions: defaultMenuIdsByRole,
  };
};
